import type { NftActivityItem } from "./types";

/**
 * ONE ROW OF A TOKEN'S ACTIVITY, read the way the response really is.
 *
 * `/nfts/{contract}/{tokenId}/activity` sends no event type, so it is read
 * from the row: no seller (the backend writes "-" or the zero address) is a
 * mint, a price is a sale, otherwise a transfer. An explicit type, if a row
 * ever carries one, wins.
 *
 * The price: `price_eth` is the decimal amount on a chain sale (where `price`
 * is in wei); on NFTBaz's own rows `price_eth` is "-" and `price` is already
 * the decimal. "-" stands for "none" in the address and hash fields too.
 */
export type ActivityKind = "mint" | "sale" | "transfer";

const ZERO = /^0x0{40}$/i;

function present(value: unknown): string | null {
  const text = value == null ? "" : String(value).trim();
  return text && text !== "-" ? text : null;
}

function address(value: unknown): string | null {
  const text = present(value);
  return text && !ZERO.test(text) ? text : null;
}

function decimal(value: unknown): number | null {
  const text = present(value);
  if (!text) return null;
  const n = Number(text);
  return Number.isFinite(n) ? n : null;
}

export function readActivityRow(a: NftActivityItem & { type?: unknown; event?: unknown; event_type?: unknown }) {
  const seller = address(a.seller?.address);
  const buyer = address(a.buyer?.address);
  const fromEth = decimal(a.price_eth);
  const local = String(a.marketplace ?? "").toLowerCase() === "nftbaz";
  // `price` is wei on a chain row, so it is only read as a decimal on NFTBaz's own.
  const amount = fromEth ?? (local ? decimal(a.price) : null);
  const explicit = String(a.type ?? a.event_type ?? a.event ?? "").toLowerCase();
  const kind: ActivityKind =
    explicit === "mint" || explicit === "sale" || explicit === "transfer" ? explicit
      : !seller ? "mint"
      : amount != null && amount > 0 ? "sale"
      : "transfer";
  const hash = present(a.tx_hash);
  return {
    kind,
    // A mint has no sale price; the figure NFTBaz writes on it names no currency, so it is left out.
    amount: kind !== "mint" && amount != null && amount > 0 ? amount : null,
    // Only an ETH figure names its coin; NFTBaz's rows state no currency.
    symbol: fromEth != null ? "ETH" : null,
    seller,
    buyer,
    tx: hash && /^0x[0-9a-f]+$/i.test(hash) ? hash : null,
  };
}

/** The endpoint answers a whole collection's sales for some contracts: only this token's are its activity. */
export function forToken(list: NftActivityItem[], tokenId: string): NftActivityItem[] {
  const id = String(tokenId);
  return list.filter((a) => a.token_id == null || String(a.token_id) === id);
}
